import { useCallback } from 'react';

export interface UseTableFiltersConfig<TFilters> {
  updateFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;
  refetch: () => void;
  refetchMetadata: () => void;
}

export interface FilterMapping {
  [columnId: string]: {
    filterKey: string;
    transform?: (values: string[]) => any;
  };
}

export function useTableFilters<TFilters>({ updateFilter, refetch, refetchMetadata }: UseTableFiltersConfig<TFilters>) {
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[], filterMapping?: FilterMapping) => {
      // If all options are selected or none are selected, clear the filter
      const isAllSelected = allOptions && selectedValues.length === allOptions.length;

      if (!selectedValues.length || isAllSelected) {
        // Clear the filter for this column using the mapping
        if (filterMapping && filterMapping[columnId]) {
          const { filterKey } = filterMapping[columnId];
          updateFilter(filterKey as keyof TFilters, undefined);
        }

        // Always refresh metadata and data after clearing filters
        refetchMetadata();
        refetch();
        return;
      }

      // Apply the filter based on column ID and mapping
      if (filterMapping && filterMapping[columnId]) {
        const { filterKey, transform } = filterMapping[columnId];
        const filterValue = transform ? transform(selectedValues) : selectedValues;
        updateFilter(filterKey as keyof TFilters, filterValue);
      }

      // Refresh data after applying filters
      refetch();
    },
    [updateFilter, refetch, refetchMetadata]
  );

  return {
    handleTableFilter,
  };
}

// Common filter mappings for different entity types
export const SERVICE_PROVIDER_FILTER_MAPPING: FilterMapping = {
  name: { filterKey: 'names' },
  code: { filterKey: 'codes' },
  parentServiceProviderId: { filterKey: 'parentServiceProviderIds' },
};

export const LEGAL_ENTITY_FILTER_MAPPING: FilterMapping = {
  name: { filterKey: 'names' },
  code: { filterKey: 'codes' },
  nationalCurrency: { filterKey: 'nationalCurrencies' },
  parentId: { filterKey: 'parentIds' },
  isBilledEntity: {
    filterKey: 'isBilledEntities',
    transform: (values: string[]) =>
      values.map((value) => {
        if (value === 'Yes') return true;
        if (value === 'No') return false;
        return null; // For 'null' or any other value
      }),
  },
};
